import { apiFetch } from '@/api/apiFetch';
import { maxBatUploadSize } from '@/config';
import { t } from '@/translations/i18n';
import { addBreadcrumb } from '@sentry/vue';

const BYTES_PER_MEGABYTE = 1_000_000;

function toMegabytes(bytes: number): string {
    return (bytes / BYTES_PER_MEGABYTE).toFixed(1);
}

/**
 * fetch() only rejects (instead of resolving with a non-2xx response) when no usable
 * HTTP response was received: connection reset during upload, 413 without CORS headers,
 * proxy timeout without CORS headers, offline... The browser always throws a TypeError here.
 */
function isNetworkError(error: unknown): boolean {
    return error instanceof TypeError;
}

async function getCMJNbat(width: number, height: number, imgBAT?: Blob, imgBATVerso?: Blob): Promise<Blob> {
    if (!width || !height || isNaN(width) || isNaN(height)) {
        throw new Error('Invalid width or height');
    }

    const rectoBytes = imgBAT?.size ?? 0;
    const versoBytes = imgBATVerso?.size ?? 0;
    const payloadBytes = rectoBytes + versoBytes;
    const uploadContext = { width, height, rectoBytes, versoBytes, payloadBytes };

    // Kept in production on purpose: lets support read the payload size from the customer's console
    console.info(`[BAT] upload payload ${toMegabytes(payloadBytes)} Mo`, uploadContext);
    addBreadcrumb({ category: 'bat', level: 'info', message: 'BAT upload started', data: uploadContext });

    if (payloadBytes > maxBatUploadSize) {
        throw new Error(t('generate_bat_too_large_error', { size: toMegabytes(payloadBytes), max: toMegabytes(maxBatUploadSize) }));
    }

    const formData = new FormData();
    if (imgBAT) {
        formData.append('file[]', imgBAT);
    }
    if (imgBATVerso) {
        formData.append('file[]', imgBATVerso);
    }
    formData.append('width', String(width));
    formData.append('height', String(height));

    const startedAt = performance.now();
    let response: Response;
    try {
        response = await apiFetch(`/pdf`, { method: 'POST', body: formData });
    } catch (e) {
        const durationMs = Math.round(performance.now() - startedAt);
        addBreadcrumb({
            category: 'bat',
            level: 'error',
            message: 'BAT upload failed without HTTP response',
            data: { ...uploadContext, durationMs, cause: String(e) },
        });
        if (isNetworkError(e)) {
            throw new Error(t('generate_bat_network_error', { size: toMegabytes(payloadBytes) }));
        }
        throw e;
    }

    const durationMs = Math.round(performance.now() - startedAt);
    addBreadcrumb({
        category: 'bat',
        level: response.ok ? 'info' : 'error',
        message: 'BAT upload answered',
        data: { ...uploadContext, durationMs, status: response.status },
    });

    if (!response.ok) {
        throw new Error(t('generate_bat_error'));
    }
    const responseBody = await response.blob();
    return responseBody;
}

export { getCMJNbat };
